import { MAX_WORKCENTER_ID, printerPosition as printerPositionSchema } from '@labeling/domain';
import { type Application, applications } from '@labeling/globals';
import { Button, Combobox, Modal, TextField } from '@lego/connect-components-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { TableSkeleton } from '@/components/table-skeleton';
import { NativeField, Table } from '../../components';
import type { TableColumn } from '../../components/table';
import { useApi } from '../../providers/api';
import { useToastContext } from '../../providers/toast';
import { useUserStore } from '../../stores/user-store';
import type { PrinterAssignmentData, PrinterData } from '../../types/types';
import { fetchAllPages } from '../../utils/pagination';
import {
  canEditPrinterAssignments,
  canEditPrinterInfo,
  canEditPrinterLocation,
  canEditPrinterWorkcenter,
  isAdmin,
} from '../../utils/permissions';
import { getResponseErrorMessage, toErrorMessage } from '../../utils/response';

export const PrintersPage = () => {
  const { t } = useTranslation();
  const { client } = useApi();
  const { user } = useUserStore();
  const [selectedPrinter, setSelectedPrinter] = useState<PrinterData | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isClearingQueue, setIsClearingQueue] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);
  const { showToast } = useToastContext();
  const [editForm, setEditForm] = useState({
    locationId: 0,
    description: '',
    assignments: [] as string[],
    mediaHeight: 0,
    mediaWidth: 0,
    cutter: false,
    clearQueueAllowed: false,
    // Kept as strings so an empty input round-trips to `null` rather than 0/''.
    workcenterId: '',
    printerPosition: '',
  });
  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);

  const {
    data: printers,
    isLoading: printersIsLoading,
    mutate: reloadPrinters,
  } = useSWR('printers', () =>
    fetchAllPages((query) => client.v1.printers.$get({ query }).then((r) => r.json()))
  );

  const { data: locations, isLoading: locationsIsLoading } = useSWR(
    'locations',
    async () => await (await client.v1.locations.$get()).json()
  );

  const {
    data: printerAssignments,
    isLoading: assignmentsIsLoading,
    mutate: reloadPrinterAssignments,
    error: assignmentsError,
  } = useSWR('printer-assignments', () =>
    fetchAllPages((query) => client.v1['printer-assignments'].$get({ query }).then((r) => r.json()))
  );

  const getLocationById = useCallback(
    (locationId: number) => {
      return locations?.items.find((loc) => loc.id === locationId);
    },
    [locations?.items]
  );

  const getPrinterAssignments = useCallback(
    (printerId: number) => {
      if (!printerAssignments?.items) return [];
      return printerAssignments.items.filter(
        (assignment: PrinterAssignmentData) => assignment.printerId === printerId
      );
    },
    [printerAssignments?.items]
  );

  const formatAssignments = useCallback(
    (printerId: number) => {
      const assignments = getPrinterAssignments(printerId);
      if (assignments.length === 0) return '-';
      return assignments
        .map((assignment: PrinterAssignmentData) => assignment.application)
        .join(', ');
    },
    [getPrinterAssignments]
  );

  const getLocationOptions = () => {
    if (!locations?.items) return [];
    return locations.items.map((location) => ({
      value: location.id.toString(),
      label: `${location.site} - ${location.area || 'No Area'}`,
    }));
  };

  const getAssignmentOptions = () => {
    return applications.map((app) => ({
      value: app,
      label: app,
    }));
  };

  /**
   * Labels are translated rather than showing the raw enum value, so the Polish
   * locale does not fall back to the English 'Left'/'Right' stored in the DB.
   */
  const printerPositionLabel = useCallback(
    (position: string) => t(`printerTable.display.printerPosition${position}`),
    [t]
  );

  const printerPositionOptions = useMemo(
    () =>
      printerPositionSchema.options.map((position) => ({
        value: position,
        label: printerPositionLabel(position),
      })),
    [printerPositionLabel]
  );

  const getPermissions = () => {
    if (!selectedPrinter) {
      return {
        canEditInfo: false,
        canEditAssignments: false,
        canEditLocation: false,
        canEditWorkcenter: false,
      };
    }

    return {
      canEditInfo: canEditPrinterInfo(user),
      canEditAssignments: canEditPrinterAssignments(user),
      canEditLocation: canEditPrinterLocation(user),
      canEditWorkcenter: canEditPrinterWorkcenter(user),
    };
  };

  const { canEditInfo, canEditAssignments, canEditLocation, canEditWorkcenter } = getPermissions();

  const canEditAnything = canEditInfo || canEditAssignments || canEditWorkcenter;

  const handlePrinterClick = (printer: PrinterData) => {
    triggerRef.current = document.activeElement as HTMLElement;
    setSelectedPrinter(printer);
    const assignments = getPrinterAssignments(printer.id);
    setEditForm({
      locationId: printer.locationId,
      description: printer.description || '',
      assignments: assignments.map((a) => a.application),
      mediaHeight: printer.mediaHeight || 0,
      mediaWidth: printer.mediaWidth || 0,
      cutter: printer.cutter || false,
      clearQueueAllowed: printer.clearQueueAllowed,
      workcenterId: printer.workcenterId?.toString() ?? '',
      printerPosition: printer.printerPosition ?? '',
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => setIsDialogOpen(false);

  /**
   * Builds the `PUT /v1/printers/:id` body from only the fields the current user
   * may change, so a `user` never sends admin/maintainer-only fields (which the
   * API would reject with a 403).
   */
  const buildPrinterUpdates = () => ({
    ...(canEditInfo
      ? {
          description: editForm.description,
          mediaHeight: editForm.mediaHeight,
          mediaWidth: editForm.mediaWidth,
          cutter: editForm.cutter,
        }
      : {}),
    ...(canEditLocation ? { locationId: editForm.locationId } : {}),
    ...(canEditWorkcenter
      ? {
          // `null` clears the field; the API rejects an all-undefined body.
          workcenterId: editForm.workcenterId.trim() === '' ? null : Number(editForm.workcenterId),
          // Parsed through the shared schema so only valid enum members are sent.
          printerPosition: printerPositionSchema.safeParse(editForm.printerPosition).data ?? null,
        }
      : {}),
  });

  /** Each step resolves to a failed `Response`, or `null` when it succeeded or was skipped. */
  const submitPrinterFields = async (printerId: number) => {
    const updates = buildPrinterUpdates();
    if (Object.keys(updates).length === 0) return null;

    const response = await client.v1.printers[':id'].$put({
      param: { id: printerId.toString() },
      json: updates,
    });
    return response.ok ? null : response;
  };

  const submitClearQueueAllowed = async (printer: PrinterData) => {
    if (user.type !== 'admin' || editForm.clearQueueAllowed === printer.clearQueueAllowed) {
      return null;
    }

    const response = await client.v1.printers[':id']['clear-queue-allowed'].$patch({
      param: { id: printer.id.toString() },
      json: { clearQueueAllowed: editForm.clearQueueAllowed },
    });
    return response.ok ? null : response;
  };

  const submitAssignments = async (printerId: number) => {
    if (!canEditAssignments) return null;

    const currentAssignments = getPrinterAssignments(printerId);
    const addedApplications = editForm.assignments.filter(
      (app) => !currentAssignments.some((a) => a.application === app)
    );
    const removedAssignments = currentAssignments.filter(
      (a) => !editForm.assignments.includes(a.application)
    );

    const responses = await Promise.all([
      ...addedApplications.map((app) =>
        client.v1['printer-assignments'].$post({
          json: { printerId, application: app as Application },
        })
      ),
      ...removedAssignments.map((assignment) =>
        client.v1['printer-assignments'][':id'].$delete({
          param: { id: assignment.id.toString() },
        })
      ),
    ]);

    return responses.find((r) => !r.ok) ?? null;
  };

  const handleSave = async () => {
    if (!selectedPrinter || !canEditAnything) return;
    const printer = selectedPrinter;

    const refreshCaches = () => Promise.all([reloadPrinters(), reloadPrinterAssignments()]);

    // Mirrors the shared `workcenterId` contract (positive int32) so a typo
    // surfaces as an inline toast instead of an opaque 400 from the API, or an
    // out-of-range error from the Postgres `integer` column. The regex rejects
    // non-digits, and the bound check rejects anything the column cannot hold —
    // digit strings above the bound always widen to a double comfortably above
    // it, so imprecision past `Number.MAX_SAFE_INTEGER` cannot mask an overflow.
    const workcenterIdRaw = editForm.workcenterId.trim();
    if (
      canEditWorkcenter &&
      workcenterIdRaw !== '' &&
      (!/^[1-9]\d*$/.test(workcenterIdRaw) || Number(workcenterIdRaw) > MAX_WORKCENTER_ID)
    ) {
      showToast(t('printerTable.response.invalidWorkcenterId'), 'error');
      return;
    }

    const steps: { run: () => Promise<Response | null>; errorKey: string }[] = [
      {
        run: () => submitPrinterFields(printer.id),
        errorKey: 'printerTable.response.printerUpdateFailed',
      },
      {
        run: () => submitClearQueueAllowed(printer),
        errorKey: 'printerTable.response.printerUpdateFailed',
      },
      {
        run: () => submitAssignments(printer.id),
        errorKey: 'printerTable.response.printerAssignmentUpdateFailed',
      },
    ];

    try {
      for (const { run, errorKey } of steps) {
        const failedResponse = await run();
        if (failedResponse) {
          const message = await getResponseErrorMessage(failedResponse, t(errorKey));
          showToast(message, 'error');
          await refreshCaches();
          return;
        }
      }

      await refreshCaches();
      showToast(t('printerTable.response.printerUpdatedSuccess'), 'success');
      handleCloseDialog();
    } catch (error) {
      showToast(toErrorMessage(error, t('printerTable.response.printerUpdateFailed')), 'error');
    }
  };

  const canClearQueue = !!selectedPrinter?.clearQueueAllowed;

  const handleClearQueue = async () => {
    if (!selectedPrinter) return;
    setIsClearingQueue(true);
    try {
      const res = await client.v1.printers[':id']['clear-queue'].$post({
        param: { id: selectedPrinter.id.toString() },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'partial') {
          const failures = data.results
            .filter((r) => !r.success)
            .map((r) => `${r.server}: ${'reason' in r ? r.reason : 'unknown'}`)
            .join('; ');
          showToast(
            t('printerTable.response.clearQueuePartial', { message: data.message, failures }),
            'warning'
          );
        } else {
          showToast(t('printerTable.response.clearQueueSuccess'), 'success');
        }
      } else {
        const message = await getResponseErrorMessage(
          res,
          t('printerTable.response.clearQueueFailed')
        );
        showToast(message, 'error');
      }
    } catch (e) {
      showToast(toErrorMessage(e, t('printerTable.response.clearQueueFailed')), 'error');
    } finally {
      setIsClearingQueue(false);
    }
  };

  const userIsAdmin = isAdmin(user);
  const tableColumns: TableColumn<PrinterData>[] = useMemo(() => {
    return [
      {
        key: 'name',
        label: t('printerTable.display.name'),
        sortable: true,
        render: (value) => <span className="text-core-slate-1300 font-medium">{value}</span>,
      },
      {
        key: 'id',
        label: t('printerTable.display.id'),
        sortable: true,
        accessor: (printer: PrinterData) => printer.id,
      },
      {
        key: 'manufacturer',
        label: t('printerTable.display.manufacturer'),
        sortable: true,
      },
      {
        key: 'model',
        label: t('printerTable.display.model'),
        sortable: true,
      },
      {
        key: 'site',
        label: t('printerTable.display.site'),
        sortable: true,
        accessor: (printer: PrinterData) => getLocationById(printer.locationId)?.site || '-',
      },
      {
        key: 'area',
        label: t('printerTable.display.area'),
        sortable: true,
        accessor: (printer: PrinterData) => getLocationById(printer.locationId)?.area || '-',
      },
      {
        key: 'workcenterId',
        label: t('printerTable.display.workcenterId'),
        sortable: true,
        accessor: (printer: PrinterData) => printer.workcenterId ?? '-',
      },
      {
        key: 'printerPosition',
        label: t('printerTable.display.printerPosition'),
        sortable: true,
        accessor: (printer: PrinterData) =>
          printer.printerPosition ? printerPositionLabel(printer.printerPosition) : '-',
      },
      {
        key: 'dimensions',
        label: t('printerTable.display.dimensions'),
        sortable: false,
        accessor: (printer: PrinterData) =>
          printer.mediaWidth && printer.mediaHeight
            ? `${printer.mediaWidth} x ${printer.mediaHeight}`
            : '-',
      },
      {
        key: 'cutter',
        label: t('printerTable.display.cutter'),
        sortable: true,
        accessor: (printer: PrinterData) => (printer.cutter ? 'True' : 'False'),
      },
      {
        key: 'assignments',
        label: t('printerTable.display.assignments'),
        sortable: false,
        accessor: (printer: PrinterData) => formatAssignments(printer.id),
      },
      ...(userIsAdmin
        ? [
            {
              key: 'clearQueueAllowed',
              label: t('printerTable.display.clearQueueAllowed'),
              sortable: true,
              accessor: (printer: PrinterData) =>
                printer.clearQueueAllowed
                  ? t('printerTable.display.clearQueueEnabled')
                  : t('printerTable.display.clearQueueDisabled'),
              render: (_value, printer) => (
                <span
                  className={`inline-flex items-center rounded-50 px-150 py-25 text-body-xxs font-bold ${
                    printer.clearQueueAllowed
                      ? 'bg-green-50 text-green-800 border border-green-300'
                      : 'bg-red-50 text-red-800 border border-red-300'
                  }`}
                >
                  {printer.clearQueueAllowed
                    ? t('printerTable.display.clearQueueEnabled')
                    : t('printerTable.display.clearQueueDisabled')}
                </span>
              ),
            } satisfies TableColumn<PrinterData>,
          ]
        : []),
    ];
  }, [getLocationById, formatAssignments, printerPositionLabel, t, userIsAdmin]);

  const isLoading = printersIsLoading || locationsIsLoading || assignmentsIsLoading;
  const assignmentsSettled = !!printerAssignments || !!assignmentsError;

  const activePrinters = useMemo(() => {
    if (!printers?.items) return [];
    return printers.items.filter((printer) => printer.deletedAt === null);
  }, [printers?.items]);

  const printerCount = activePrinters.length;

  if (!printers || !locations || !assignmentsSettled || isLoading) {
    return <TableSkeleton columns={tableColumns.length} page />;
  }

  return (
    <>
      <div className="px-400 py-400">
        <h1 className="text-heading-md font-brand font-bold text-core-slate-1300 mb-300">
          {`${t(printerCount > 1 ? 'printerTable.display.printers' : 'printerTable.display.printer')} (${printerCount})`}
        </h1>

        <Table
          data={activePrinters}
          columns={tableColumns}
          onRowClick={handlePrinterClick}
          loading={isLoading}
          emptyMessage={t('printerTable.response.noPrintersFound')}
          searchPlaceholder={t('printerTable.action.searchPrintersPlaceholder')}
          ariaLabel={t('printerTable.display.printers')}
        />
      </div>

      <Modal
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        triggerRef={triggerRef}
        size="medium"
      >
        <Modal.Title>
          {`${canEditAnything ? t('printerTable.action.editPrinterTitle') : t('printerTable.action.viewPrinterTitle')}${selectedPrinter ? `: ${selectedPrinter.name}` : ''}`}
        </Modal.Title>
        <Modal.Content>
          {selectedPrinter && (
            <div
              ref={setPortalTarget}
              className="grid grid-cols-1 sm:grid-cols-2 gap-200 text-body-xs"
            >
              <div>
                <span className="font-brand font-bold text-core-slate-1100">
                  {t('printerTable.display.name')}:
                </span>
                <p className="text-core-slate-1300">{selectedPrinter.name}</p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100">
                  {t('printerTable.display.manufacturer')}:
                </span>
                <p className="text-core-slate-1300">{selectedPrinter.manufacturer}</p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100">
                  {t('printerTable.display.model')}:
                </span>
                <p className="text-core-slate-1300">{selectedPrinter.model}</p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100">
                  {t('printerTable.display.site')}:
                </span>
                <p className="text-core-slate-1300">
                  {getLocationById(selectedPrinter.locationId)?.site || '-'}
                </p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printerTable.display.location')}:
                </span>
                <Combobox
                  fullWidth
                  value={editForm.locationId ? [editForm.locationId.toString()] : []}
                  onChange={(_e, items) =>
                    setEditForm({ ...editForm, locationId: Number(items[0]?.value ?? 0) })
                  }
                  placeholder={t('printerTable.action.selectLocation')}
                  size="medium"
                  disabled={!canEditLocation}
                  {...(portalTarget ? { portalTarget } : {})}
                >
                  {getLocationOptions().map((o) => (
                    <Combobox.Option key={o.value} value={o.value}>
                      {o.label}
                    </Combobox.Option>
                  ))}
                </Combobox>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printerTable.display.assignments')}:
                </span>
                <Combobox
                  fullWidth
                  value={editForm.assignments}
                  onChange={(_e, items) =>
                    setEditForm({ ...editForm, assignments: items.map((i) => i.value) })
                  }
                  placeholder={t('printerTable.action.selectAssignments')}
                  size="medium"
                  multiple
                  disabled={!canEditAssignments}
                  {...(portalTarget ? { portalTarget } : {})}
                >
                  {getAssignmentOptions().map((o) => (
                    <Combobox.Option key={o.value} value={o.value}>
                      {o.label}
                    </Combobox.Option>
                  ))}
                </Combobox>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printerTable.display.workcenterId')}:
                </span>
                <NativeField
                  type="number"
                  size="medium"
                  value={editForm.workcenterId}
                  onChange={(e) => setEditForm({ ...editForm, workcenterId: e.target.value })}
                  placeholder={t('printerTable.action.enterWorkcenterId')}
                  disabled={!canEditWorkcenter}
                />
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printerTable.display.printerPosition')}:
                </span>
                <Combobox
                  fullWidth
                  value={editForm.printerPosition ? [editForm.printerPosition] : []}
                  onChange={(_e, items) =>
                    setEditForm({ ...editForm, printerPosition: items[0]?.value ?? '' })
                  }
                  placeholder={t('printerTable.action.selectPrinterPosition')}
                  size="medium"
                  showClearButton
                  disabled={!canEditWorkcenter}
                  {...(portalTarget ? { portalTarget } : {})}
                >
                  {printerPositionOptions.map((o) => (
                    <Combobox.Option key={o.value} value={o.value}>
                      {o.label}
                    </Combobox.Option>
                  ))}
                </Combobox>
              </div>

              <div className="sm:col-span-2">
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printerTable.display.description')}:
                </span>
                <TextField
                  value={editForm.description}
                  onChange={(_e, value) => setEditForm({ ...editForm, description: value })}
                  placeholder={t('printerTable.action.enterDescription')}
                  size="medium"
                  disabled={!canEditInfo}
                />
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printerTable.display.mediaWidth')}:
                </span>
                <NativeField
                  type="number"
                  size="medium"
                  value={editForm.mediaWidth.toString()}
                  onChange={(e) =>
                    setEditForm({ ...editForm, mediaWidth: Number(e.target.value) || 0 })
                  }
                  placeholder={t('printerTable.action.enterWidth')}
                  disabled={!canEditInfo}
                />
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printerTable.display.mediaHeight')}:
                </span>
                <NativeField
                  type="number"
                  size="medium"
                  value={editForm.mediaHeight.toString()}
                  onChange={(e) =>
                    setEditForm({ ...editForm, mediaHeight: Number(e.target.value) || 0 })
                  }
                  placeholder={t('printerTable.action.enterHeight')}
                  disabled={!canEditInfo}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="flex items-center gap-200">
                  <input
                    type="checkbox"
                    checked={editForm.cutter}
                    onChange={(e) => setEditForm({ ...editForm, cutter: e.target.checked })}
                    disabled={!canEditInfo}
                    className="w-150 h-150 text-core-blue-900 bg-core-white border-25 border-core-slate-100 rounded-50 focus:ring-core-blue-900 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className="font-brand font-bold text-core-slate-1100">
                    {t('printerTable.display.cutter')}
                  </span>
                </label>
              </div>

              {userIsAdmin && (
                <div className="sm:col-span-2">
                  <label className="flex items-center gap-200">
                    <input
                      type="checkbox"
                      checked={editForm.clearQueueAllowed}
                      onChange={(e) =>
                        setEditForm({ ...editForm, clearQueueAllowed: e.target.checked })
                      }
                      className="w-150 h-150 text-core-blue-900 bg-core-white border-25 border-core-slate-100 rounded-50 focus:ring-core-blue-900 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="font-brand font-bold text-core-slate-1100">
                      {t('printerTable.action.allowClearQueue')}
                    </span>
                  </label>
                </div>
              )}

              {canClearQueue && (
                <div className="col-span-2 mt-200 pt-200 border-t border-core-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-brand font-bold text-core-slate-1100">
                        Clear Print Queue
                      </span>
                      <p className="text-core-slate-1000 text-body-xxs">
                        Remove all queued jobs from this printer
                      </p>
                    </div>
                    <Button
                      variant="negative"
                      size="small"
                      onClick={handleClearQueue}
                      loading={isClearingQueue}
                      disabled={isClearingQueue}
                      loadingText="Clearing..."
                    >
                      Clear Queue
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Footer>
          <Button variant="neutral" size="small" onClick={handleCloseDialog}>
            {canEditAnything ? t('printerTable.action.cancel') : t('printerTable.action.close')}
          </Button>
          {canEditAnything && (
            <Button variant="primary" size="small" onClick={handleSave}>
              {t('printerTable.action.saveChanges')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
